/**
 * Сводка проблем кассы для единого блока «Проблемы».
 *
 * Правила намеренно отделены от UI: кассир видит одну свёрнутую строку,
 * а порядок, цвет и число проблем считаются здесь и покрыты тестами.
 */

export type ProblemSeverity = 'critical' | 'warning' | 'info'

export type PosProblemId =
  | 'hold'
  | 'replacement'
  | 'kkt_mismatch'
  | 'kkt_external'
  | 'kkt_state'
  | 'ofd'
  | 'fn'
  | 'pending_fiscal'
  | 'marking_off'
  | 'scales'

export interface PosProblemInput {
  /** Чеки на проверке маркировки: деньги в ящике уже есть, смена без решения не закроется. */
  heldCount: number
  /** Чеки без ККТ, ждущие замены на фискальные. */
  replacementCount: number
  /** Смена POS и ККТ не согласованы. */
  kktMismatch: boolean
  /** Смену ККТ открыло другое ПО. */
  externalKktShift: boolean
  /** Состояние ККТ из драйвера. */
  kktState?: 'ok' | 'paper_out' | 'shift_closed' | 'error' | 'no_device'
  /** Документы ОФД, которые не удалось отправить. */
  ofdUnsent?: number
  /** Остаток срока действия ФН в днях. */
  fnDaysLeft?: number | null
  /** Чеки, отложенные к пробитию на ККТ (офлайн). */
  pendingFiscal?: number
  /** Модуль маркировки выключен. */
  markingDisabled?: boolean
  /** Весы включены, но не подключены. */
  scalesDisconnected?: boolean
}

export interface PosProblem {
  id: PosProblemId
  severity: ProblemSeverity
  /** Заголовок строки в блоке. */
  title: string
  /** Число однотипных проблем (например, «Замены: 2»). */
  count?: number
  /** Подпись главного действия. */
  actionLabel?: string
  /** Нужен PIN администратора для действия. */
  requiresAdmin?: boolean
}

const SEVERITY_WEIGHT: Record<ProblemSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2
}

/**
 * Смысловой порядок внутри тяжести. Hold выше замены: деньги в ящике
 * уже есть, и решение нельзя откладывать. Счётчик сортировку не решает.
 */
const PROBLEM_ORDER: Record<PosProblemId, number> = {
  hold: 0,
  replacement: 1,
  kkt_mismatch: 2,
  kkt_external: 3,
  kkt_state: 4,
  ofd: 5,
  fn: 6,
  pending_fiscal: 7,
  marking_off: 8,
  scales: 9
}

/** Отсортированный список проблем: критичные → предупреждения → инфо. */
export function buildPosProblems(input: PosProblemInput): PosProblem[] {
  const problems: PosProblem[] = []

  if (input.heldCount > 0) {
    problems.push({
      id: 'hold',
      severity: 'critical',
      title: 'Чеки на проверке маркировки',
      count: input.heldCount,
      actionLabel: 'Открыть'
    })
  }

  if (input.replacementCount > 0) {
    problems.push({
      id: 'replacement',
      severity: 'critical',
      title: 'Замена чеков без ККТ',
      count: input.replacementCount,
      actionLabel: 'Печать фискального'
    })
  }

  if (input.kktMismatch) {
    problems.push({
      id: 'kkt_mismatch',
      severity: 'critical',
      title: 'Смена POSPoint и ККТ не согласованы',
      actionLabel: 'Согласовать',
      requiresAdmin: true
    })
  }

  if (input.externalKktShift) {
    problems.push({
      id: 'kkt_external',
      severity: 'warning',
      title: 'Смена ККТ открыта другим ПО',
      actionLabel: 'X-отчёт'
    })
  }

  if (input.kktState && input.kktState !== 'ok') {
    const text = {
      paper_out: 'В ККТ закончилась бумага',
      shift_closed: 'Смена ККТ закрыта',
      error: 'ККТ сообщает об ошибке',
      no_device: 'ККТ не подключена'
    }[input.kktState]
    problems.push({
      id: 'kkt_state',
      severity: input.kktState === 'error' || input.kktState === 'paper_out' ? 'critical' : 'warning',
      title: text
    })
  }

  if ((input.ofdUnsent ?? 0) > 0) {
    problems.push({
      id: 'ofd',
      severity: 'warning',
      title: 'Документы не отправлены в ОФД',
      count: input.ofdUnsent
    })
  }

  if (typeof input.fnDaysLeft === 'number') {
    if (input.fnDaysLeft <= 0) {
      problems.push({ id: 'fn', severity: 'critical', title: 'Срок действия ФН истёк' })
    } else if (input.fnDaysLeft <= 3) {
      problems.push({
        id: 'fn',
        severity: 'warning',
        title: 'Срок действия ФН истекает',
        count: input.fnDaysLeft
      })
    }
  }

  if ((input.pendingFiscal ?? 0) > 0) {
    // Очередь на ККТ — рабочее состояние, а не проблема: она не должна
    // увеличивать счётчик «Уведомлений» (считаем только critical + warning).
    problems.push({
      id: 'pending_fiscal',
      severity: 'info',
      title: 'Чеки ждут пробития на ККТ',
      count: input.pendingFiscal
    })
  }

  if (input.markingDisabled) {
    problems.push({ id: 'marking_off', severity: 'info', title: 'Маркировка отключена' })
  }

  if (input.scalesDisconnected) {
    problems.push({ id: 'scales', severity: 'info', title: 'Весы не подключены' })
  }

  return problems.sort((a, b) => {
    const bySeverity = SEVERITY_WEIGHT[a.severity] - SEVERITY_WEIGHT[b.severity]
    if (bySeverity !== 0) return bySeverity
    return PROBLEM_ORDER[a.id] - PROBLEM_ORDER[b.id]
  })
}

/**
 * Число для бейджа: только проблемы (critical + warning).
 * Информационные уведомления (очередь ККТ, отключённая маркировка) бейдж
 * не раздувают — иначе кассир видит «3 проблемы» там, где ничего не сломано.
 */
export function countActionableProblems(problems: PosProblem[]): number {
  return problems
    .filter((p) => p.severity !== 'info')
    .reduce((sum, p) => sum + (p.count ?? 1), 0)
}

/** Самая тяжёлая проблема набора. `null` — проблем нет. */
export function highestSeverity(problems: PosProblem[]): ProblemSeverity | null {
  if (problems.length === 0) return null
  if (problems.some((p) => p.severity === 'critical')) return 'critical'
  if (problems.some((p) => p.severity === 'warning')) return 'warning'
  return 'info'
}

export type ProblemsTone = 'danger' | 'warn' | 'neutral'

/** Цвет свёрнутой строки: только тяжесть, без количества. */
export function problemsTone(severity: ProblemSeverity | null): ProblemsTone {
  if (severity === 'critical') return 'danger'
  if (severity === 'warning') return 'warn'
  return 'neutral'
}

/**
 * Нужно ли подсветить блок: подсвечиваем при появлении критичной проблемы
 * и только один раз за её «эпизод» (ключ — набор критичных id).
 */
export function criticalSignature(problems: PosProblem[]): string {
  return problems
    .filter((p) => p.severity === 'critical')
    .map((p) => p.id)
    .sort()
    .join('|')
}
